import React, { useId, useRef, useState } from 'react';

import { Check, Search } from 'lucide-react';

import { useClickOutside } from '@src/corelib/hooks';

import { resolveEnter } from './autocompleteKeys';
import { fieldFrame } from './fieldFrame';
import { Spinner } from './Spinner';
import { cn } from './utils';


/** Default single-line option renderer shared by the value/label bridges
 * (OptionAutocomplete, SuggestAutocomplete): a truncating 13px ink label. */
function OptionLabel(option) {
  return <span className="truncate text-table text-ink">{option.label}</span>;
}


/**
 * Autocomplete combobox per the DESIGN.md `autocomplete` spec (Pencil frame
 * D9CHZ "State · Search & select"): surface-1 search control with a leading
 * magnifier, a floating listbox of option rows, and an optional pinned footer
 * action.
 *
 * The primitive is presentation-only: it NEVER fetches or filters. The caller
 * owns search/debounce/fetch via `query`/`onQueryChange` and passes the rendered
 * `options`.
 *
 * Overlay choice: this uses a custom absolutely-positioned panel rather than
 * @radix-ui/react-popover (which IS a dependency, see ui/Popover.jsx). Radix
 * Popover installs a focus trap that steals focus from the text input, which
 * breaks the `aria-activedescendant` pattern this combobox relies on (focus must
 * stay on the input while the listbox is navigated). A custom panel keeps focus
 * on the input and lets us drive active-option highlighting from the keyboard.
 *
 * a11y: input is `role="combobox"` with `aria-expanded`/`aria-controls`/
 * `aria-activedescendant`/`aria-autocomplete="list"`/`aria-haspopup="listbox"`;
 * the panel is `role="listbox"`; rows are `role="option"` with `aria-selected`.
 */
const Autocomplete = React.forwardRef((props, ref) => {
  const {
    value = null,
    onChange,
    query = '',
    onQueryChange,
    options = [],
    getOptionLabel = (o) => o?.label ?? '',
    getOptionSubtitle,
    getOptionKey = (o, i) => o?.id ?? o?.value ?? getOptionLabel(o) ?? i,
    renderOption,
    loading = false,
    placeholder,
    emptyText = 'No results',
    footer,
    disabled = false,
    invalid = false,
    // F3/AUTOORBIT-505 opt-in: when false, do NOT pre-highlight the first filtered
    // row (start at -1) so Enter can't silently commit a partial substring match.
    // Defaults true to keep every existing consumer behaviourally identical.
    autoHighlightFirst = true,
    onBlur,
    id,
    className,
    ...restProps
  } = props;

  const [open, setOpen] = useState(false);

  // The row to highlight when the option set / query / autoHighlight mode changes:
  // first row when autoHighlightFirst is on and there are options, else none (F3).
  const resetIndex = autoHighlightFirst && options.length > 0 ? 0 : -1;
  const [activeIndex, setActiveIndex] = useState(resetIndex);
  // Reset the active row by DERIVING it at render (not via a setState-in-effect,
  // which forced an extra commit on every keystroke in this shared primitive).
  // `resetKey` tracks the exact references the old effect depended on; when any
  // differs, adjust the state during render — React re-renders immediately, before
  // paint, so the highlight never flashes stale. Autocomplete's OWN re-renders
  // (keyboard/mouse moving activeIndex) keep the same `options` reference, so they
  // don't trip the reset; only a parent passing a new option set / query does.
  const [resetKey, setResetKey] = useState({ options, query, autoHighlightFirst });
  if (resetKey.options !== options
    || resetKey.query !== query
    || resetKey.autoHighlightFirst !== autoHighlightFirst) {
    setResetKey({ options, query, autoHighlightFirst });
    setActiveIndex(resetIndex);
  }

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const setRef = (node) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    }
    else if (ref) {
      ref.current = node;
    }
  };

  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const optionId = (index) => `${reactId}-option-${index}`;

  // Close on click-outside — the shared corelib hook only binds while `open`.
  useClickOutside(containerRef, () => setOpen(false), open);

  const selectedKey = value != null ? getOptionKey(value, -1) : undefined;

  const isSelected = (option, index) => (
    value != null && getOptionKey(option, index) === selectedKey
  );

  const selectOption = (option) => {
    onChange?.(option);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) {
          return;
        }
        setActiveIndex((prev) => (prev + 1) % options.length);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) {
          return;
        }
        setActiveIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
        break;
      }
      case 'End': {
        if (open && options.length > 0) {
          event.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      }
      case 'Enter': {
        // While the dropdown is open, Enter must NEVER fall through to a form's
        // implicit submit (F2). Closed: stay presentation-neutral and let Enter
        // behave normally — the consuming form owns stray-submit guarding.
        const { action, index } = resolveEnter({
          open, activeIndex, options, autoHighlightFirst, query, getOptionLabel,
        });
        if (action === 'ignore') {
          break;
        }
        event.preventDefault();
        if (action === 'select' || action === 'selectOnly') {
          selectOption(options[index]);
        }
        break;
      }
      case 'Escape': {
        if (open) {
          event.preventDefault();
          setOpen(false);
          inputRef.current?.focus();
        }
        break;
      }
      case 'Home': {
        if (open && options.length > 0) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      }
      case 'Tab': {
        setOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const activeOptionId = (open && activeIndex >= 0 && activeIndex < options.length)
    ? optionId(activeIndex)
    : undefined;

  const showEmpty = open && !loading && options.length === 0;
  const hasFooter = footer != null;
  const footerNode = typeof footer === 'function' ? footer() : footer;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          fieldFrame({ focus: 'within', invalid }),
          'flex h-10 w-full items-center gap-2 px-3',
          disabled && 'cursor-not-allowed opacity-45',
        )}
      >
        <Search className="size-4 shrink-0 text-ink-subtle" />

        <input
          ref={setRef}
          id={id}
          type="text"
          value={query}
          disabled={disabled}
          aria-expanded={open}
          aria-activedescendant={activeOptionId}
          className={cn(
            'flex-1 border-0 bg-transparent text-sm text-ink',
            'placeholder:text-ink-subtle outline-none',
            'disabled:cursor-not-allowed',
          )}
          role="combobox"
          onBlur={onBlur}
          autoComplete="off"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onChange={(event) => onQueryChange?.(event.target.value)}
          {...restProps}
        />

        {loading
          ? <Spinner size="sm" className="shrink-0" />
          : null}
      </div>

      {open
        ? (
          <div
            className={cn(
              'absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden',
              'rounded-lg border border-hairline bg-surface-1 shadow-popover',
            )}
          >
            <ul
              id={listboxId}
              className="max-h-72 overflow-y-auto"
              role="listbox"
            >
              {loading
                ? (
                  <li className="flex items-center gap-2.5 px-3 py-2.5 text-table text-ink-subtle">
                    <Spinner size="sm" />
                    Loading…
                  </li>
                )
                : null}

              {showEmpty
                ? (
                  <li className="px-3 py-2.5 text-table text-ink-subtle">
                    {emptyText}
                  </li>
                )
                : null}

              {!loading && options.map((option, index) => {
                const active = index === activeIndex;
                const selected = isSelected(option, index);

                return (
                  <li
                     
                    id={optionId(index)}
                    aria-selected={selected}
                    key={getOptionKey(option, index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 px-3 py-2.5',
                      active && 'bg-surface-2',
                    )}
                    role="option"
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      // Prevent the input from blurring before the click lands.
                      event.preventDefault();
                      selectOption(option);
                    }}
                  >
                    {renderOption
                      ? renderOption(option, { active, selected })
                      : (
                        <>
                          <span className="size-[26px] shrink-0 rounded-full bg-surface-3" />

                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-table text-ink">
                              {getOptionLabel(option)}
                            </span>

                            {getOptionSubtitle
                              ? (
                                <span className="truncate text-xs text-ink-subtle">
                                  {getOptionSubtitle(option)}
                                </span>
                              )
                              : null}
                          </span>
                        </>
                      )}

                    {selected
                      ? <Check className="ml-auto size-4 shrink-0 text-primary" />
                      : null}
                  </li>
                );
              })}
            </ul>

            {hasFooter
              ? (
                <>
                  <div className="h-px bg-hairline" />

                  <div
                    className={cn(
                      'flex items-center gap-2 bg-primary-soft px-3 py-2.5',
                      'text-table font-medium text-primary-on-dark',
                    )}
                  >
                    {footerNode}
                  </div>
                </>
              )
              : null}
          </div>
        )
        : null}
    </div>
  );
});

Autocomplete.displayName = 'Autocomplete';

export { Autocomplete, OptionLabel };
export default Autocomplete;
